import { getAuthoredDescriptor } from '../builder-v2/index.ts';

interface Point3 {
  x: number;
  y: number;
  z: number;
}

interface Point2 {
  x: number;
  y: number;
}

const cubic = (p0: Point3, p1: Point3, p2: Point3, p3: Point3, t: number): Point3 => {
  const inverse = 1 - t;
  return {
    x:
      inverse ** 3 * p0.x +
      3 * inverse ** 2 * t * p1.x +
      3 * inverse * t ** 2 * p2.x +
      t ** 3 * p3.x,
    y:
      inverse ** 3 * p0.y +
      3 * inverse ** 2 * t * p1.y +
      3 * inverse * t ** 2 * p2.y +
      t ** 3 * p3.y,
    z:
      inverse ** 3 * p0.z +
      3 * inverse ** 2 * t * p1.z +
      3 * inverse * t ** 2 * p2.z +
      t ** 3 * p3.z,
  };
};

const cubicTangent = (p0: Point3, p1: Point3, p2: Point3, p3: Point3, t: number): Point3 => {
  const inverse = 1 - t;
  return {
    x:
      3 * inverse ** 2 * (p1.x - p0.x) +
      6 * inverse * t * (p2.x - p1.x) +
      3 * t ** 2 * (p3.x - p2.x),
    y:
      3 * inverse ** 2 * (p1.y - p0.y) +
      6 * inverse * t * (p2.y - p1.y) +
      3 * t ** 2 * (p3.y - p2.y),
    z:
      3 * inverse ** 2 * (p1.z - p0.z) +
      6 * inverse * t * (p2.z - p1.z) +
      3 * t ** 2 * (p3.z - p2.z),
  };
};

const normalize = (point: Point3): Point3 => {
  const length = Math.hypot(point.x, point.y, point.z) || 1;
  return { x: point.x / length, y: point.y / length, z: point.z / length };
};

const cross = (left: Point3, right: Point3): Point3 => ({
  x: left.y * right.z - left.z * right.y,
  y: left.z * right.x - left.x * right.z,
  z: left.x * right.y - left.y * right.x,
});

const project = (point: Point3): Point2 => ({
  x: point.x + point.z * 0.58,
  y: -point.y + point.z * 0.25,
});

const toPath = (points: readonly Point2[]): string =>
  points
    .map((point, index) => `${index ? 'L' : 'M'}${point.x.toFixed(2)},${point.y.toFixed(2)}`)
    .join(' ');

export function AuthoredPieceThumbnail({ descriptorId }: { descriptorId: string }) {
  const descriptor = getAuthoredDescriptor(descriptorId);
  if (!descriptor) return null;
  const samples: { left: Point3; right: Point3 }[] = [];
  let start: Point3 = { x: 0, y: 0, z: 0 };
  descriptor.spans.forEach((span, spanIndex) => {
    const steps = 9;
    for (let step = spanIndex ? 1 : 0; step < steps; step += 1) {
      const t = step / (steps - 1);
      const center = cubic(start, span.control1, span.control2, span.end, t);
      const tangent = normalize(cubicTangent(start, span.control1, span.control2, span.end, t));
      let side = normalize(cross({ x: 0, y: 1, z: 0 }, tangent));
      if (Math.hypot(side.x, side.y, side.z) < 0.1) side = { x: 0, y: 0, z: 1 };
      const up = normalize(cross(tangent, side));
      const bank =
        (span.bankStartDegrees ?? 0) +
        ((span.bankEndDegrees ?? 0) - (span.bankStartDegrees ?? 0)) * t;
      const winding =
        (span.windingStartDegrees ?? 0) +
        ((span.windingEndDegrees ?? 0) - (span.windingStartDegrees ?? 0)) * t;
      const radians = ((bank + winding) * Math.PI) / 180;
      const railOffset = {
        x: side.x * Math.cos(radians) + up.x * Math.sin(radians),
        y: side.y * Math.cos(radians) + up.y * Math.sin(radians),
        z: side.z * Math.cos(radians) + up.z * Math.sin(radians),
      };
      const halfGauge = 4;
      samples.push({
        left: {
          x: center.x - railOffset.x * halfGauge,
          y: center.y - railOffset.y * halfGauge,
          z: center.z - railOffset.z * halfGauge,
        },
        right: {
          x: center.x + railOffset.x * halfGauge,
          y: center.y + railOffset.y * halfGauge,
          z: center.z + railOffset.z * halfGauge,
        },
      });
    }
    start = span.end;
  });
  const projected = samples.flatMap((sample) => [project(sample.left), project(sample.right)]);
  const minX = Math.min(...projected.map((point) => point.x));
  const maxX = Math.max(...projected.map((point) => point.x));
  const minY = Math.min(...projected.map((point) => point.y));
  const maxY = Math.max(...projected.map((point) => point.y));
  const width = Math.max(maxX - minX, 1);
  const height = Math.max(maxY - minY, 1);
  const scale = Math.min(104 / width, 52 / height);
  const mapPoint = (point: Point2): Point2 => ({
    x: 60 + (point.x - (minX + maxX) / 2) * scale,
    y: 32 + (point.y - (minY + maxY) / 2) * scale,
  });
  const leftRail = samples.map((sample) => mapPoint(project(sample.left)));
  const rightRail = samples.map((sample) => mapPoint(project(sample.right)));
  const ties = samples
    .filter((_, index) => index % 4 === 0)
    .map((sample) => ({
      left: mapPoint(project(sample.left)),
      right: mapPoint(project(sample.right)),
    }));

  return (
    <svg
      class="bc-authored-piece-thumbnail"
      viewBox="0 0 120 64"
      role="img"
      aria-label={`${descriptor.label} track preview`}
    >
      <rect width="120" height="64" rx="9" fill="#081A28" />
      {ties.map((tie, index) => (
        <line
          key={index}
          x1={tie.left.x}
          y1={tie.left.y}
          x2={tie.right.x}
          y2={tie.right.y}
          stroke="#5F7782"
          stroke-width="1.3"
        />
      ))}
      <path d={toPath(leftRail)} fill="none" stroke="#173B8F" stroke-width="3.6" />
      <path d={toPath(rightRail)} fill="none" stroke="#173B8F" stroke-width="3.6" />
      <path d={toPath(leftRail)} fill="none" stroke="#5D95FF" stroke-width="1.25" />
      <path d={toPath(rightRail)} fill="none" stroke="#5D95FF" stroke-width="1.25" />
      <circle cx={leftRail[0]?.x} cy={leftRail[0]?.y} r="2.2" fill="#73E6CF" />
      <circle cx={rightRail.at(-1)?.x} cy={rightRail.at(-1)?.y} r="2.2" fill="#F5C451" />
    </svg>
  );
}
